import { useEffect, useState, useCallback, useMemo } from "react";
import api, { dkk, num, dec, parseAmount } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer, LineChart, Line, Tooltip, XAxis, CartesianGrid, YAxis,
} from "recharts";
import {
  TrendingUp, ArrowUpRight, ArrowDownRight, MoonStar, FileDown, FileText,
  Search, ArrowUpDown, ChevronUp, ChevronDown, Loader2, Trophy, Calculator, Flame,
} from "lucide-react";

const FAL = "#FB923C";
const RIN = "#38BDF8";

const MONTHS_DA = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
function fmtPeriod(p) {
  if (!p || typeof p !== "string") return p;
  if (p.includes("-U")) return "U" + p.split("-U")[1];
  const parts = p.split("-");
  if (parts.length === 3) return `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}`;
  if (parts.length === 2) return MONTHS_DA[parseInt(parts[1], 10) - 1] || p;
  return p;
}

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function csvCell(v) {
  const s = String(v ?? "");
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/* ---------------- Butiks-sammenligning over tid ---------------- */
function CompareTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-white border border-[#e2e8f0] px-3 py-2 text-xs shadow-sm">
      <p className="text-slate-500 mb-1">{fmtPeriod(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="ml-auto font-semibold text-slate-800">{dkk(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function CompareRow({ label, fal, rin, fmt }) {
  const total = (fal || 0) + (rin || 0);
  const falPct = total ? Math.round(((fal || 0) / total) * 100) : 0;
  const rinPct = 100 - falPct;
  const leader = (fal || 0) === (rin || 0) ? null : (fal || 0) > (rin || 0) ? "falster" : "ringsted";
  return (
    <div className="py-3 border-b border-[#e2e8f0] last:border-0" data-testid={`compare-row-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
        {leader && (
          <span className="text-xs font-medium" style={{ color: leader === "falster" ? FAL : RIN }}>
            {leader === "falster" ? "Falster fører" : "Ringsted fører"}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="flex items-center gap-2 text-slate-700">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: FAL }} /> {fmt(fal)}
        </span>
        <span className="flex items-center gap-2 text-slate-700">
          {fmt(rin)} <span className="w-2.5 h-2.5 rounded-full" style={{ background: RIN }} />
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden flex bg-[#e2e8f0]">
        <div className="h-full" style={{ width: `${falPct}%`, background: FAL }} />
        <div className="h-full" style={{ width: `${rinPct}%`, background: RIN }} />
      </div>
    </div>
  );
}

function Comparison({ dateParams }) {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const today = new Date();
    let from = dateParams?.from, to = dateParams?.to, gran = dateParams?.gran;
    if (!from || !to) {
      const s = new Date(); s.setDate(today.getDate() - 29);
      from = iso(s); to = iso(today); gran = "day";
    }
    try {
      const [s, t] = await Promise.all([
        api.get("/kpi/summary", { params: { from, to, store: "all" } }),
        api.get("/kpi/trend", { params: { from, to, granularity: gran || "day" } }),
      ]);
      setSummary(s.data); setTrend(t.data);
    } finally { setLoading(false); }
  }, [dateParams]);

  useEffect(() => { load(); }, [load]);

  const fal = summary?.stores?.falster;
  const rin = summary?.stores?.ringsted;

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="insights-comparison">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Butikker over tid</h3>
        <span className="text-xs text-slate-500 ml-auto">{summary?.range ? `${summary.range.from} → ${summary.range.to}` : ""}</span>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: FAL }} /> Falster</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RIN }} /> Ringsted</span>
              <span className="ml-auto">Omsætning pr. periode</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} minTickGap={22} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip content={<CompareTip />} />
                <Line type="monotone" dataKey="falster_revenue" name="Falster" stroke={FAL} strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="ringsted_revenue" name="Ringsted" stroke={RIN} strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2">
            <CompareRow label="Omsætning" fal={fal?.revenue} rin={rin?.revenue} fmt={dkk} />
            <CompareRow label="Antal salg" fal={fal?.orders} rin={rin?.orders} fmt={num} />
            <CompareRow label="Gns. kurv" fal={fal?.aov} rin={rin?.aov} fmt={dkk} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Ligger stille ---------------- */
function StaleProducts() {
  const [store, setStore] = useState("all");
  const [minDays, setMinDays] = useState("30");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "days_since", dir: "desc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/insights/stale-products", { params: { store, min_days: parseInt(minDays, 10) || 30 } });
      setRows(data);
    } finally { setLoading(false); }
  }, [store, minDays]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: k === "product" || k === "category" ? "asc" : "desc" }));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q ? rows.filter((r) => r.product.toLowerCase().includes(q) || r.category.toLowerCase().includes(q) || String(r.productno).toLowerCase().includes(q)) : [...rows];
    out.sort((a, b) => {
      const k = sort.key;
      let r;
      if (k === "product" || k === "category" || k === "last_sold") r = String(a[k]).localeCompare(String(b[k]), "da");
      else r = (a[k] || 0) - (b[k] || 0);
      return sort.dir === "asc" ? r : -r;
    });
    return out;
  }, [rows, query, sort]);

  const totalRevenue = useMemo(() => filtered.reduce((s, r) => s + (r.total_revenue || 0), 0), [filtered]);

  const exportCSV = () => {
    const headers = ["Vare", "Varenr", "Kategori", "Butik", "Sidst solgt", "Dage siden", "Antal solgt", "På lager", "Omsætning i alt"];
    const lines = [headers.join(";")];
    filtered.forEach((r) => lines.push([
      r.product, r.productno, r.category, (r.stores || []).join("/"), r.last_sold, r.days_since, r.total_qty, r.stock == null ? "" : r.stock, String(r.total_revenue).replace(".", ","),
    ].map(csvCell).join(";")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ligger-stille-${iso(new Date())}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const rowsHtml = filtered.map((r) => `<tr><td>${r.product}</td><td>${r.category}</td><td>${(r.stores || []).join("/")}</td><td>${r.last_sold}</td><td style="text-align:right">${r.days_since}</td><td style="text-align:right">${r.total_qty}</td><td style="text-align:right">${r.stock == null ? "—" : r.stock}</td><td style="text-align:right">${dkk(r.total_revenue)}</td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html lang="da"><head><meta charset="utf-8"><title>Ligger stille</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;padding:24px}h1{font-size:18px;margin:0 0 4px}p{color:#64748b;font-size:12px;margin:0 0 16px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0}th{text-transform:uppercase;letter-spacing:.06em;font-size:10px;color:#64748b}</style>
      </head><body><h1>Ligger stille — varer uden salg i ${minDays}+ dage</h1>
      <p>Butik: ${store === "all" ? "Begge butikker" : store} · ${filtered.length} varer · genereret ${new Date().toLocaleString("da-DK")}</p>
      <table><thead><tr><th>Vare</th><th>Kategori</th><th>Butik</th><th>Sidst solgt</th><th>Dage siden</th><th>Antal</th><th>På lager</th><th>Omsætning</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      </body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  const SortHead = ({ k, children, align = "left" }) => (
    <th className={`font-medium py-2 px-2 cursor-pointer select-none hover:text-slate-700 ${align === "right" ? "text-right" : "text-left"}`} onClick={() => toggleSort(k)} data-testid={`stale-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>{children}{sort.key === k ? (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}</span>
    </th>
  );

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="insights-stale">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MoonStar className="w-4 h-4 text-slate-500" />
          <h3 className="font-heading text-lg font-semibold text-slate-900">Ligger stille</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 w-[150px]" data-testid="stale-store-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-lg bg-white border-[#e2e8f0] text-slate-700">
              <SelectItem value="all">Begge butikker</SelectItem>
              <SelectItem value="falster">Falster</SelectItem>
              <SelectItem value="ringsted">Ringsted</SelectItem>
            </SelectContent>
          </Select>
          <Select value={minDays} onValueChange={setMinDays}>
            <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 w-[150px]" data-testid="stale-days-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-lg bg-white border-[#e2e8f0] text-slate-700">
              <SelectItem value="14">14+ dage</SelectItem>
              <SelectItem value="30">30+ dage</SelectItem>
              <SelectItem value="60">60+ dage</SelectItem>
              <SelectItem value="90">90+ dage</SelectItem>
              <SelectItem value="180">180+ dage</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportCSV} className="h-9 rounded-lg bg-white border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900 gap-1.5" data-testid="stale-export-csv" disabled={filtered.length === 0}>
            <FileDown className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF} className="h-9 rounded-lg bg-white border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900 gap-1.5" data-testid="stale-export-pdf" disabled={filtered.length === 0}>
            <FileText className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søg vare, varenr. eller kategori…" className="h-9 pl-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="stale-search-input" />
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-10 text-center" data-testid="stale-empty">Ingen varer ligger stille i den valgte periode. 🎉</p>
      ) : (
        <>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
            <span data-testid="stale-count">{filtered.length} varer uden salg i {minDays}+ dage</span>
            <span>Samlet historisk omsætning: <span className="font-semibold text-slate-700">{dkk(totalRevenue)}</span></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-700" data-testid="stale-table">
              <thead className="text-slate-500 border-b border-[#e2e8f0]">
                <tr>
                  <SortHead k="product">Vare</SortHead>
                  <SortHead k="category">Kategori</SortHead>
                  <th className="font-medium py-2 px-2 text-left">Butik</th>
                  <SortHead k="last_sold">Sidst solgt</SortHead>
                  <SortHead k="days_since" align="right">Dage siden</SortHead>
                  <SortHead k="total_qty" align="right">Antal solgt</SortHead>
                  <SortHead k="stock" align="right">På lager</SortHead>
                  <SortHead k="total_revenue" align="right">Omsætning</SortHead>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.productno} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]" data-testid={`stale-row-${r.productno}`}>
                    <td className="py-2 px-2 font-medium text-slate-800">{r.product}</td>
                    <td className="py-2 px-2 text-slate-500">{r.category}</td>
                    <td className="py-2 px-2 text-slate-500 capitalize">{(r.stores || []).join(", ")}</td>
                    <td className="py-2 px-2 tabular-nums text-slate-600">{r.last_sold}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      <span className={`font-semibold ${r.days_since >= 90 ? "text-red-600" : r.days_since >= 60 ? "text-amber-600" : "text-slate-700"}`}>{r.days_since}</span>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-600">{num(r.total_qty)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-700">{r.stock == null ? "—" : num(r.stock)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-medium text-slate-800">{dkk(r.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Bedst sælgende ---------------- */
function BestSellers({ dateParams }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const today = new Date();
    let from = dateParams?.from, to = dateParams?.to;
    if (!from || !to) { const s = new Date(); s.setDate(today.getDate() - 29); from = iso(s); to = iso(today); }
    try {
      const { data } = await api.get("/kpi/products", { params: { store: "all", from, to, limit: 40 } });
      const clean = (data || []).filter((p) => !String(p.product || "").trim().toLowerCase().startsWith("tilbudspris")).slice(0, 15);
      setRows(clean);
    } finally { setLoading(false); }
  }, [dateParams]);

  useEffect(() => { load(); }, [load]);

  const maxRev = Math.max(...rows.map((r) => r.revenue || 0), 1);

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="insights-bestsellers">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Bedst sælgende</h3>
        <span className="text-xs text-slate-500 ml-auto">Top 15 · efter omsætning i perioden</span>
      </div>
      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-10 text-center" data-testid="bestsellers-empty">Ingen synkroniserede varedata for perioden endnu.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-700" data-testid="bestsellers-table">
            <thead className="text-slate-500 border-b border-[#e2e8f0]">
              <tr>
                <th className="font-medium py-2 px-2 text-left w-8">#</th>
                <th className="font-medium py-2 px-2 text-left">Vare</th>
                <th className="font-medium py-2 px-2 text-left">Kategori</th>
                <th className="font-medium py-2 px-2 text-right">Antal solgt</th>
                <th className="font-medium py-2 px-2 text-left w-[26%]">Omsætning</th>
                <th className="font-medium py-2 px-2 text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.product}-${i}`} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]" data-testid={`bestseller-row-${i}`}>
                  <td className="py-2 px-2 tabular-nums text-slate-400 font-semibold">{i + 1}</td>
                  <td className="py-2 px-2 font-medium text-slate-800">{r.product}</td>
                  <td className="py-2 px-2 text-slate-500">{r.category}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-600">{num(r.units)}</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded bg-[#e2e8f0] overflow-hidden min-w-[60px]">
                        <div className="h-full rounded" style={{ width: `${((r.revenue || 0) / maxRev) * 100}%`, background: RIN }} />
                      </div>
                      <span className="tabular-nums font-medium text-slate-800 whitespace-nowrap">{dkk(r.revenue)}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-emerald-600">{r.margin == null ? "—" : `${r.margin}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Avance-beregner ---------------- */
function AvanceCalculator() {
  const VAT = 1.25;
  const [kost, setKost] = useState("");
  const [salgEx, setSalgEx] = useState("");
  const [salgIncl, setSalgIncl] = useState("");
  const [antal, setAntal] = useState("1");

  const onSalgEx = (v) => { setSalgEx(v); const n = parseAmount(v); setSalgIncl(Number.isFinite(n) ? dec(n * VAT) : ""); };
  const onSalgIncl = (v) => { setSalgIncl(v); const n = parseAmount(v); setSalgEx(Number.isFinite(n) ? dec(n / VAT) : ""); };

  const k = parseAmount(kost) || 0;
  const sEx = parseAmount(salgEx) || 0;
  const qty = Math.max(0, parseInt(antal, 10) || 0);
  const profit = sEx - k;
  const avancePct = sEx > 0 ? (profit / sEx) * 100 : 0;
  const paaslagPct = k > 0 ? (profit / k) * 100 : 0;

  const results = [
    { label: "Salgspris (ex. moms)", value: `${dec(sEx)} kr.` },
    { label: "Fortjeneste pr. enhed", value: `${dec(profit)} kr.`, accent: profit >= 0 ? "pos" : "neg" },
    { label: "Avance i procent", value: `${dec(avancePct)} %`, accent: "hi" },
    { label: "Påslag (ift. kostpris)", value: `${dec(paaslagPct)} %` },
    { label: "Samlet fortjeneste", value: `${dec(profit * qty)} kr.`, accent: profit >= 0 ? "pos" : "neg" },
    { label: "Omsætning (ex. moms)", value: `${dec(sEx * qty)} kr.` },
    { label: "Omsætning (inkl. moms)", value: `${dec(sEx * VAT * qty)} kr.` },
  ];

  const accentClass = (a) => a === "pos" ? "text-emerald-600" : a === "neg" ? "text-red-600" : a === "hi" ? "text-sky-700" : "text-slate-900";

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="insights-avance">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Avance-beregner</h3>
        <span className="text-xs text-slate-500 ml-auto">Regn altid ex. moms · dansk moms 25%</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Kostpris (ex. moms)</label>
            <Input value={kost} onChange={(e) => setKost(e.target.value)} inputMode="decimal" placeholder="0,00" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="avance-kostpris" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Antal enheder</label>
            <Input value={antal} onChange={(e) => setAntal(e.target.value)} inputMode="numeric" placeholder="1" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="avance-antal" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Salgspris (ex. moms)</label>
            <Input value={salgEx} onChange={(e) => onSalgEx(e.target.value)} inputMode="decimal" placeholder="0,00" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="avance-salg-ex" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Salgspris (inkl. moms)</label>
            <Input value={salgIncl} onChange={(e) => onSalgIncl(e.target.value)} inputMode="decimal" placeholder="0,00" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="avance-salg-incl" />
          </div>
        </div>
        <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Resultat</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {results.map((r) => (
              <div key={r.label} className="flex items-baseline justify-between gap-2 border-b border-[#e2e8f0] pb-2 last:border-0" data-testid={`avance-res-${r.label}`}>
                <dt className="text-sm text-slate-500">{r.label}</dt>
                <dd className={`text-sm font-bold tabular-nums ${accentClass(r.accent)}`}>{r.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Time-for-time heatmap ---------------- */
function WeekHourHeatmap({ dateParams }) {
  const [store, setStore] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const today = new Date();
    let from = dateParams?.from, to = dateParams?.to;
    if (!from || !to) { const s = new Date(); s.setDate(today.getDate() - 27); from = iso(s); to = iso(today); }
    try { const { data } = await api.get("/kpi/hourly", { params: { store, from, to } }); setData(data); }
    finally { setLoading(false); }
  }, [store, dateParams]);
  useEffect(() => { load(); }, [load]);

  const hours = data?.hours || [];
  const grid = data?.grid || [];
  let maxRev = 0, peak = null;
  grid.forEach((row) => (row.hours || []).forEach((c) => {
    if (c.avg_revenue > maxRev) maxRev = c.avg_revenue;
    if (!peak || c.avg_revenue > peak.rev) peak = { rev: c.avg_revenue, wd: row.label, hour: c.hour };
  }));
  const cellBg = (v) => { if (!v || maxRev <= 0) return "#f1f5f9"; return `rgba(2,132,199,${(0.1 + 0.9 * (v / maxRev)).toFixed(3)})`; };

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="insights-heatmap">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Hvornår sælger I mest</h3>
        <Select value={store} onValueChange={setStore}>
          <SelectTrigger className="h-8 w-[140px] rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 text-xs ml-auto" data-testid="heatmap-store"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-[#e2e8f0] text-slate-700">
            <SelectItem value="all">Begge butikker</SelectItem>
            <SelectItem value="falster">Falster</SelectItem>
            <SelectItem value="ringsted">Ringsted</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="h-56 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : grid.length === 0 || hours.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid="heatmap-empty">Ingen time-data for perioden. Vælg et større datointerval øverst.</p>
      ) : (
        <>
          {peak && (
            <p className="text-xs text-slate-500 mb-3">Travlest i snit: <span className="font-semibold text-slate-800">{peak.wd} kl. {String(peak.hour).padStart(2, "0")}</span> (~{dkk(peak.rev)}/time)</p>
          )}
          <div className="overflow-x-auto">
            <table className="border-separate" style={{ borderSpacing: "3px" }} data-testid="heatmap-table">
              <thead>
                <tr>
                  <th className="w-10"></th>
                  {hours.map((h) => <th key={h.hour} className="text-[10px] font-medium text-slate-400 tabular-nums pb-1">{String(h.hour).padStart(2, "0")}</th>)}
                </tr>
              </thead>
              <tbody>
                {grid.map((row) => (
                  <tr key={row.weekday}>
                    <td className="text-[11px] font-medium text-slate-500 pr-2 whitespace-nowrap">{row.label.slice(0, 3)}</td>
                    {hours.map((h) => {
                      const c = (row.hours || []).find((x) => x.hour === h.hour) || { avg_revenue: 0, avg_orders: 0 };
                      return (
                        <td key={h.hour}>
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded" style={{ background: cellBg(c.avg_revenue) }}
                            title={`${row.label} kl. ${String(h.hour).padStart(2, "0")}: ${dkk(c.avg_revenue)} · ${c.avg_orders} salg`}
                            data-testid={`heatmap-${row.weekday}-${h.hour}`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
            <span>Mindre</span>
            <span className="w-4 h-3 rounded" style={{ background: cellBg(maxRev * 0.15) }} />
            <span className="w-4 h-3 rounded" style={{ background: cellBg(maxRev * 0.5) }} />
            <span className="w-4 h-3 rounded" style={{ background: cellBg(maxRev) }} />
            <span>Mere (gns. omsætning pr. time)</span>
          </div>
        </>
      )}
    </div>
  );
}

export function InsightsView({ dateParams }) {
  return (
    <div className="space-y-4" data-testid="insights-view">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <ArrowUpRight className="w-4 h-4" />
        <span>Indsigt &amp; beslutning · sammenlign butikker, se bedst sælgende og find varer der binder kapital</span>
      </div>
      <AvanceCalculator />
      <Comparison dateParams={dateParams} />
      <WeekHourHeatmap dateParams={dateParams} />
      <BestSellers dateParams={dateParams} />
      <StaleProducts />
    </div>
  );
}

export default InsightsView;
